import React, { useState, useCallback, useEffect, useMemo, BaseSyntheticEvent, PropsWithChildren } from "react";
import { Content } from "@iventis/translations/content/typed-content";
import { Divider, LinearProgress, Select, SelectChangeEvent, useTheme } from "@mui/material";
import { IGridComponent } from "@iventis/interactive-grid";
import type { IGridItem } from "@iventis/interactive-grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { screenSizeBreakpoints } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { Attention } from "@iventis/styles/src/components/texts";
import { useMachine } from "@xstate/react";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { Session } from "@iventis/calendar/components/types";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { Node } from "@iventis/domain-model/model/node";
import { findNode } from "@iventis/tree-browser/tree-operations";
import { compareAsc } from "date-fns";
import { shallowEqual, useSelector } from "react-redux";
import { allOptionsValueLiteral, StyledItemSelect, StyledMenuItem, SwitchWithLabel, StyledCustomDialog } from "@iventis/components";
import { utcToZonedTime } from "date-fns-tz";
import { CalendarButton, CalendarButtonsContainer } from "@iventis/calendar/components/zoom-container";
import { AuthState } from "@iventis/permissions/src/auth.slice";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import {
    dateToTimeSpan,
    queryParams,
    useConstant,
    useWindowSize,
    withinBounds,
    canEdit,
    lowestAuthorisation,
    API_KEY_QUERY_PARAM_KEY,
    EMPTY_GUID,
    downloadCSV,
} from "@iventis/utilities";
import { MicroService } from "@iventis/api-helpers";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { toast } from "@iventis/toasts/src/toast";
import { AuthenticatedUser } from "@iventis/domain-model/model/authenticatedUser";
import { ScheduleConfigNoPermission } from "@iventis/schedule-config";
import { FilterFormat, TimeSpan } from "@iventis/types";
import { useResourceConnection } from "@iventis/api/src/use-resource-connection";
import { ResourceTypes } from "@iventis/api/src/connection.slice";
import { getApiBaseUrl, scheduleApi } from "@iventis/api/src/api";
import { IconButtonComponent } from "@iventis/components/src/icon-button";
import { IventisErrorBoundaryWithToast } from "@iventis/error-boundaries";
import { IventisFilterOperator } from "@iventis/domain-model/model/iventisFilterOperator";
import { scheduleMachine as createScheduleMachine } from "./schedule.machine";
import SideBarToggleComponent from "./sidebar-toggle";
import { CreateSubScheduleComponent } from "./create-sub-schedule";
import { CreateScheduleFormWizard } from "./create-schedule";
import { removeUnusedGroups } from "./calendar-helper-functions";
import {
    findScheduleNode,
    flattenVenueLocations,
    groupsFromDays,
    dayNumberFromDate,
    hierarchyToSummaryGroups,
    pairVenueIdWithScheduleVersionIdFromSessions,
    getScheduleVersionDataFieldValues,
    groupByDataField,
    findInheritedDataFieldValue,
    renderDataFieldSelectedText,
    venueSubGroupIdGenerator,
    groupByDay,
    fillEmptyGroups,
    getScheduleVersionColour,
    generateUndefinedGroupId,
    findInheritedVenue,
    groupByVenue,
    GetVenueGroupsTranslatedText,
    findTopCalendarlevelEventBlock,
    sortGroups,
    includeThirdLevelVenue,
    isFilterTypeCalendar,
    CalendarRange,
    getCalendarRangeBasedOnFilterType,
} from "./schedule-helper-functions";
import { ScheduleVersionNode, ScheduleWizardDisplayed, SummaryColumnGroup } from "./schedule.types";
import { ScheduleTable } from "./schedule-table";
import { SportEventUnitRenderer, SportSessionRenderer } from "./calendar-render-components";
import { eventBlockHierarchyToCalendarSportSessionGroupedByDay, eventBlockHierarchyToCalendarSportSessionViewByDay } from "./domain-to-calendar-transformer";
import { FilterType } from "./filter-rules";
import { SubEventCrudOperation } from "./sub-event-form-wrapper";
import {
    IGridItemEventBlock,
    renderGridItem,
    RenderOriginHeader,
    renderSummaryCluster,
    renderSummaryColumnHeader,
    renderSummaryDiscipline,
    renderSummaryVenue,
} from "./summary-renderers";
import { isSessionAMedalEvent, ViewType } from "./sport-event-helpers";
import { PopOverByCords } from "./popover-by-cords";
import { SportSessionCard } from "./session-card";
import CreateSessionComponent from "./session-dialog";
import { ScheduleEditModal } from "./edit-schedule";
import { findHierarchyEntityById, getParentHierarchyTreeByIdAsArray } from "./hierarchy.helpers";
import {
    StyledErrorLarge,
    StyledSchedule,
    StyledTableWrapper,
    StyledPage,
    StyledMainSection,
    HeadSection,
    StyledGroupedRow,
    StyledViewBy,
    StyledDaySelector,
    StyledSelector,
    StyledDataFieldSelector,
    StyledCalendarHeaderButtonLarge,
    representationToggleIcons,
    StyledContentHeaders,
    StyledCalendarHeaderButtonSmall,
    StyledMainContentContainer,
} from "./schedule.styles";
import { RunSheet } from "./run-sheet";
import { getEventBlockTypeByCalendarLevel } from "./event-block-helpers";
import { SubEventDialog } from "./sub-event-dialog";
import { showEventBlockOnCalendar } from "./filter-helper-functions";
import { ImportEventBlocks } from "./import-event-blocks";
import { KeyPopoverComponent } from "./key-popover";
import { ProjectScheduleConfig } from "./project-schedule-config";
import { EditScheduleDataOptions } from "./edit-schedule-data-options";
import { ScheduleQueryParam, updateUrlSearchParam, useLocationDataWithSubject } from "./schedule-url-helpers";
import { useScheduleUrlSubscription } from "./schedule-url-subscription";
import { ScheduleSidebar } from "./schedule-sidebar";
import { useScheduleVersionPermission } from "./schedule-hooks";
import { SportEventDataFieldNames } from "./datafields";
import { VenueSelector, VenueSelectorButton } from "./venue-selector";

const SchedulePage: React.FC<PropsWithChildren> = ({ children }) => {
    const translate = useIventisTranslate();

    const styledTheme: Theme = useTheme<Theme>();

    const [history, locationData$] = useLocationDataWithSubject();

    const scheduleMachine = useConstant(() => createScheduleMachine(history, locationData$));

    const [state, send] = useMachine(scheduleMachine, {
        context: {
            onScheduleCopyCompleted,
            isTableView: locationData$.getValue().query.get(ScheduleQueryParam.Table) === "true",
        },
    });

    useScheduleUrlSubscription(send, locationData$);

    const [dialogSelectedHierarchy, setDialogSelectedHierarchy] = useState<Hierarchy<EventBlock>>(null);

    const [miniPopOver, setMiniPopOver] = useState<{ x: number; y: number; eventBlockHierarchySelected: Hierarchy<EventBlock> }>(null);

    const {
        schedule,
        scheduleNode,
        selectedScheduleVersionRootId,
        selectedScheduleVersionHierarchy,
        venueLocations,
        eventBlockHierarchy,
        selectedVenueLocations,
        availableViewByOptions,
        selectedViewByOption,
        availableDataFieldListItems,
        selectedDataFieldListItems,
        selectedSubScheduleIds,
        day,
        pendingSchedules,
        availableDays,
        historicalNodeCreatedEvents,
        isTableView,
        calendarLevel,
    } = state.context;

    // Calendar level 0 represents "No events". This is used in summary view only, so showSessions = false when calendar level = 0
    const showSessions = calendarLevel > 0;

    const setShowSessions = (show: boolean) => send({ type: "UPDATE_CALENDAR_LEVEL", payload: show ? 1 : 0 });

    const setIsTableView = (is: boolean) => send({ type: "UPDATE_IS_TABLE_VIEW", payload: is });

    const selectedScheduleNode: ScheduleVersionNode = useMemo(() => findScheduleNode([scheduleNode as ScheduleVersionNode], selectedScheduleVersionRootId), [
        scheduleNode,
        selectedScheduleVersionRootId,
    ]);

    const selectedSubSchedule = useMemo(() => selectedScheduleVersionHierarchy?.children.find((child) => selectedSubScheduleIds.includes(child.item.id)), [
        selectedSubScheduleIds,
        selectedScheduleVersionHierarchy,
    ]);

    const selectedSession = useMemo(() => findHierarchyEntityById(eventBlockHierarchy?.children, dialogSelectedHierarchy?.item?.id), [
        eventBlockHierarchy,
        dialogSelectedHierarchy,
    ]);

    const { calendarStartPosition, calendarEndPosition }: CalendarRange = useMemo(() => {
        if (schedule == null) {
            return {} as CalendarRange;
        }
        return getCalendarRangeBasedOnFilterType(selectedViewByOption?.filterType, availableDays, schedule.dayStartAt, schedule.dayEndAt, day, schedule.timezone);
    }, [schedule, day, selectedViewByOption, availableDays]);

    const { calendarStartPosition: urlStartPosition, calendarEndPosition: urlEndPosition }: CalendarRange = useMemo(() => {
        const start = locationData$.getValue().query.get(ScheduleQueryParam.CalendarStart);
        const end = locationData$.getValue().query.get(ScheduleQueryParam.CalendarEnd);
        if (start == null || end == null || schedule == null || !withinBounds([start, end], [schedule.dayStartAt as TimeSpan, schedule.dayEndAt as TimeSpan])) {
            // Only define calendar range from url if the range exists in the url
            return {} as CalendarRange;
        }
        return getCalendarRangeBasedOnFilterType(selectedViewByOption?.filterType, availableDays, start, end, day, schedule.timezone);
    }, [locationData$, schedule, day, selectedViewByOption, availableDays]);

    const { screenWidth } = useWindowSize();

    function onScheduleCopyCompleted(scheduleVersion: { id: string; name: string }) {
        toast.success({
            button: { label: translate(Content.schedule3.visitSchedule) },
            title: translate(Content.common.success),
            message: translate(Content.common.toasts.creation_completed, { resource: scheduleVersion.name }),
            onClick: () => send({ type: "SELECT_ROOT_SCHEDULE_VERSION", payload: scheduleVersion.id }),
        });
    }

    const setOpenResources = useResourceConnection();

    useEffect(() => {
        // Open the root schedule from the get-go, so that we're ready for web socket events if they come down before a schedule creation POST return
        if (schedule?.id !== undefined) {
            setOpenResources([{ resourceId: schedule.id, sourceId: schedule.id, resourceType: ResourceTypes.SCHEDULE_VERSION }]);
        }
    }, [schedule]);

    const currentUser: AuthenticatedUser = useSelector((state: { auth: AuthState }) => state.auth.user, shallowEqual);

    // if the visibility value for one of the schedule version changes
    // instead of mapping all schedule versions visibility values and checking if there is a change
    // use a useState as we know when a visibility change will take place
    const [scheduleVersionVisibilityChanged, setScheduleVersionVisibilityChanged] = useState(false);

    const setCalendarLevel = (level: number) => send({ type: "UPDATE_CALENDAR_LEVEL", payload: level });

    const showEventUnits = calendarLevel === 2;

    const [wizard, setWizard] = useState<{ type: ScheduleWizardDisplayed; id?: string }>(null);

    const [keyAnchor, setKeyAnchor] = useState<HTMLAnchorElement>(null);

    const theme = useTheme<Theme>();

    const isLoading = useMemo(
        () =>
            state.matches("scheduleLoading") ||
            state.matches("scheduleLoadingInitial") ||
            state.matches("scheduleLoadingEventBlock") ||
            state.matches("scheduleLoadingScheduleVersions") ||
            state.matches("loadViewByDataField"),
        [state.value]
    );

    const isErrored = useMemo(
        () =>
            state.matches("scheduleLoadingError") ||
            state.matches("scheduleLoadingInitialError") ||
            state.matches("scheduleLoadingEventBlockError") ||
            state.matches("scheduleLoadingScheduleVersionsError"),
        [state.value]
    );

    const noSchedule = useMemo(() => state.matches("noSchedule"), [state.value]);
    const waitingForScheduleChanges = useMemo(() => state.matches("waitingForScheduleChanges"), [state.value]);

    const selectedRootScheduleIsPending = useMemo(() => {
        const pendingSchedule = pendingSchedules.find((pending) => pending.rootScheduleVersion.id === selectedScheduleVersionRootId);
        return (pendingSchedule?.scheduleVersionsPending.length > 0 || pendingSchedule ? !pendingSchedule.rootNodeExists : false) && pendingSchedule.isRootScheduleCreation;
    }, [pendingSchedules.length, selectedScheduleVersionRootId]);

    const errorElement = useMemo(() => <StyledErrorLarge text={translate(Content.schedule.error_loading_schedule)} />, [translate]);

    /**
     * Permission root schedule version (e.g. Competition v1)
     */
    const scheduleVersionPermission = useScheduleVersionPermission(state.context);

    const renderPopUp = (x: number, y: number, eventBlockHierarchy: Hierarchy<EventBlock>) => {
        switch (eventBlockHierarchy?.item?.type?.calendarLevel) {
            case 1:
                setMiniPopOver({
                    x,
                    y,
                    eventBlockHierarchySelected: eventBlockHierarchy,
                });
                break;
            default:
                break;
        }
    };

    const filteredEventBlockHierarchy = useMemo(() => {
        setScheduleVersionVisibilityChanged(false);
        // get the selected schedule version's node
        const selectedScheduleVersion: ScheduleVersionNode = scheduleNode?.childNodes?.find(({ id }) => id === selectedScheduleVersionRootId);
        if (eventBlockHierarchy?.children != null && selectedScheduleVersion) {
            // get the children of the selected schedule version which are visible
            const visibleScheduleVersions: ScheduleVersionNode[] = selectedScheduleVersion.childNodes.filter((node: ScheduleVersionNode) => node.visible === true);

            // filter the event block if the schedule version it belongs to is not visible
            const filtered = eventBlockHierarchy.children.filter(({ item }) => {
                const eventBlock: EventBlock = item as EventBlock;
                return visibleScheduleVersions.some(({ id }) => id === eventBlock.scheduleVersionId);
            });
            return filtered;
        }
        return [];
        // either eventBlockHierarchy or scheduleVersionVisibilityChanged changes value
    }, [eventBlockHierarchy?.children, scheduleVersionVisibilityChanged, isTableView, wizard?.type, selectedScheduleVersionRootId, showEventUnits]);

    useEffect(() => {
        // If the popup's event block is no longer in the hierarchy, close the popup
        if (miniPopOver?.eventBlockHierarchySelected && !findHierarchyEntityById(filteredEventBlockHierarchy, miniPopOver.eventBlockHierarchySelected.item.id)) {
            setMiniPopOver(null);
        }
    }, [filteredEventBlockHierarchy]);

    const closeSidebar = useCallback(() => setShowSidebar(false), []);
    const onScheduleVisibilityChanged = useCallback(() => setScheduleVersionVisibilityChanged(true), []);

    const calendarSessions = useMemo(() => {
        if (selectedScheduleVersionHierarchy == null) {
            return [];
        }
        const filterType = state.context.selectedViewByOption?.filterType;
        switch (filterType) {
            case FilterType.DayVenue: {
                const sessions = eventBlockHierarchyToCalendarSportSessionViewByDay(
                    filteredEventBlockHierarchy,
                    (eventBlock: Hierarchy<EventBlock>) => {
                        const venue = eventBlock.item.venueLocation;
                        const highestVenue = findInheritedVenue(eventBlock, filteredEventBlockHierarchy);
                        if (highestVenue == null) {
                            return null;
                        }
                        return venue ? venue.id : generateUndefinedGroupId(highestVenue.id);
                    },
                    selectedScheduleVersionHierarchy,
                    filterType,
                    ViewType.Venue,
                    schedule.timezone
                );
                return sessions;
            }
            case FilterType.DayDataField: {
                const sessions = eventBlockHierarchyToCalendarSportSessionViewByDay(
                    filteredEventBlockHierarchy,
                    (eventBlock: Hierarchy<EventBlock>) => {
                        const dataFieldValueId = findInheritedDataFieldValue(
                            eventBlock,
                            state.context.selectedViewByOption.dataField?.id,
                            eventBlockHierarchy,
                            selectedScheduleVersionHierarchy
                        )?.id;

                        if (dataFieldValueId == null) {
                            return null;
                        }

                        return venueSubGroupIdGenerator(
                            eventBlock.item.venueLocation?.id || generateUndefinedGroupId(findInheritedVenue(eventBlock, filteredEventBlockHierarchy).id),
                            dataFieldValueId
                        );
                    },
                    selectedScheduleVersionHierarchy,
                    filterType,
                    ViewType[state.context.selectedViewByOption?.dataField?.name],
                    schedule.timezone
                );
                return sessions;
            }
            case FilterType.Venue: {
                return eventBlockHierarchyToCalendarSportSessionGroupedByDay(
                    filteredEventBlockHierarchy,
                    selectedScheduleVersionHierarchy,
                    state.context.availableDays,
                    (eventBlock) => {
                        const day = dayNumberFromDate(state.context.availableDays, eventBlock.item.startAt);
                        return `${day.toString()}_date`;
                    },
                    ViewType.Venue,
                    schedule.timezone
                );
            }
            case FilterType.DataField: {
                const sessions = eventBlockHierarchyToCalendarSportSessionGroupedByDay(
                    filteredEventBlockHierarchy,
                    selectedScheduleVersionHierarchy,
                    state.context.availableDays,
                    (eventBlock) => {
                        const day = dayNumberFromDate(state.context.availableDays, eventBlock.item.startAt);
                        return venueSubGroupIdGenerator(
                            eventBlock.item.venueLocation?.id || generateUndefinedGroupId(findInheritedVenue(eventBlock, filteredEventBlockHierarchy).id),
                            day.toString()
                        );
                    },
                    ViewType[state.context.selectedViewByOption?.dataField?.name],
                    schedule.timezone
                );
                return sessions;
            }
            default:
                return [];
        }
    }, [
        filteredEventBlockHierarchy,
        selectedScheduleVersionHierarchy,
        state.context.availableDays,
        state.context.selectedViewByOption,
        venueLocations,
        showEventUnits,
        isTableView,
    ]);

    const xGroups = useMemo(() => {
        if (selectedScheduleVersionHierarchy == null || eventBlockHierarchy == null) {
            return [];
        }
        const venueGroupsTranslatedText: GetVenueGroupsTranslatedText = {
            noSpaceDefined: translate(Content.schedule.no_space_defined),
            noSessionsInView: translate(Content.schedule.no_sessions_in_view),
        };
        switch (state.context.selectedViewByOption?.filterType) {
            case FilterType.DayVenue: {
                const groups =
                    venueLocations.length > 0
                        ? groupByVenue(venueLocations, filteredEventBlockHierarchy, showEventUnits, venueGroupsTranslatedText, state.context.schedule?.config?.venueLocationTypes)
                        : [];
                const haveClusters = includeThirdLevelVenue(schedule?.config?.venueLocationTypes);
                const showBottomLevel = haveClusters && showEventUnits;
                const toplevelOnly = !haveClusters && !showEventUnits;
                return sortGroups(toplevelOnly ? groups : fillEmptyGroups(groups, showBottomLevel));
            }
            case FilterType.DayDataField: {
                const groups = groupByDataField(
                    state.context.selectedViewByOption.dataField?.id,
                    selectedDataFieldListItems,
                    eventBlockHierarchy,
                    selectedScheduleVersionHierarchy,
                    showEventUnits,
                    venueGroupsTranslatedText
                );
                return sortGroups(fillEmptyGroups(groups, showEventUnits));
            }
            case FilterType.Venue: {
                return groupsFromDays(state.context.availableDays, schedule.timezone);
            }
            case FilterType.DataField: {
                const groups = groupByDay(eventBlockHierarchy, state.context.availableDays, showEventUnits, venueGroupsTranslatedText, schedule.timezone);
                return fillEmptyGroups(groups, showEventUnits);
            }
            case FilterType.Summary: {
                return Array.from(state.context.availableDays).map(
                    ([dayNumber, date]) =>
                        ({
                            groupId: dayNumber.toString(),
                            groupName: dayNumber.toString(),
                            dayNumber,
                            date,
                            subGroups: [],
                        } as SummaryColumnGroup)
                );
            }
            default: {
                return [];
            }
        }
    }, [
        state.context.availableDays,
        state.context.selectedViewByOption,
        venueLocations,
        calendarSessions,
        showEventUnits,
        selectedScheduleVersionHierarchy,
        schedule?.config?.venueLocationTypes,
    ]);

    // SUMMARY VIEW

    const summaryGridItems: IGridItem[] = useMemo(() => {
        if (!selectedScheduleVersionHierarchy) {
            return [];
        }
        const scheduleVersionDisciplines = getScheduleVersionDataFieldValues(selectedScheduleVersionHierarchy, SportEventDataFieldNames.DISCIPLINE);
        const gridItems = filteredEventBlockHierarchy
            .filter((eventBlock) => showEventBlockOnCalendar(state.context, eventBlock.item))
            .map(
                (eventBlock) =>
                    ({
                        yGroupId: eventBlock.item.venueLocation.id + scheduleVersionDisciplines[eventBlock.item.scheduleVersionId].id,
                        xGroupId: dayNumberFromDate(state.context.availableDays, eventBlock.item.startAt).toString(),
                        id: eventBlock.item.id,
                        eventBlock,
                    } as IGridItemEventBlock)
            )
            .sort((prevEvent, nextEvent) => compareAsc(prevEvent.eventBlock.item.startAt, nextEvent.eventBlock.item.startAt));

        if (showSessions) {
            return gridItems;
        }

        // Produce one grid item (session) for each day-venue combination, prioritising medal event sessions
        return Object.values(
            gridItems.reduce((uniqueGroupItems, gridItem) => {
                // We are uniquely grouping on xGroups and yGroups (like a composite key)
                const uniqueKey = gridItem.xGroupId + gridItem.yGroupId;
                const existingSingularItem = uniqueGroupItems[uniqueKey];

                // If there is no item for this xGroup-yGroup combination, or the existing item is not a medal event
                if (!existingSingularItem || (existingSingularItem && !isSessionAMedalEvent(existingSingularItem.eventBlock.item))) {
                    // Set that xGroup-yGroup value to that of the grid item
                    return { ...uniqueGroupItems, [uniqueKey]: gridItem };
                }

                return uniqueGroupItems;
            }, {} as { [unqueKey: string]: IGridItemEventBlock })
        );
    }, [filteredEventBlockHierarchy, showSessions]);

    const yGroups = useMemo(() => {
        if (state.context.selectedViewByOption === undefined) {
            return undefined;
        }

        if (state.context.selectedViewByOption.filterType === FilterType.Summary) {
            if (!selectedScheduleVersionHierarchy) {
                // Data not yet loaded
                return [];
            }

            const tempDirtyHierarchy = selectedScheduleVersionHierarchy;
            tempDirtyHierarchy.children = tempDirtyHierarchy.children.filter((s) => s.item);

            const venueIdScheduleVersionIds = pairVenueIdWithScheduleVersionIdFromSessions(filteredEventBlockHierarchy);
            const scheduleVersionDisciplines = getScheduleVersionDataFieldValues(tempDirtyHierarchy, SportEventDataFieldNames.DISCIPLINE);
            const groups = removeUnusedGroups(
                hierarchyToSummaryGroups(venueLocations, venueIdScheduleVersionIds, scheduleVersionDisciplines, state.context.schedule?.config.venueLocationTypes),
                summaryGridItems,
                (gridItem) => gridItem.yGroupId
            );
            return sortGroups(groups, "groupCode");
        }

        // No Y groups for any other filter type. Calendar handles time on the Y axis.
        return undefined;
    }, [state.context.selectedViewByOption, venueLocations, summaryGridItems]);

    const MainContent = useMemo(() => {
        if (isErrored) {
            return errorElement;
        }

        if (noSchedule) {
            if (currentUser.projectAdmin) {
                return (
                    <IventisErrorBoundaryWithToast toast={toast} action="reload" size="large">
                        <ProjectScheduleConfig
                            title={translate(Content.schedule2.scheduleDoesntExist.createTitle)}
                            description={translate(Content.schedule2.scheduleDoesntExist.createDescription)}
                            onSaved={(schedule) => send({ type: "SET_SCHEDULE", payload: schedule })}
                        />
                    </IventisErrorBoundaryWithToast>
                );
            }
            return <ScheduleConfigNoPermission />;
        }

        const loadingBar = <LinearProgress style={{ width: "100%", marginTop: "10px" }} />;

        if (waitingForScheduleChanges) {
            return loadingBar;
        }
        if (selectedRootScheduleIsPending) {
            return loadingBar;
        }

        if (isLoading) {
            return loadingBar;
        }

        if (!isTableView && !isLoading) {
            if (state.context.selectedViewByOption?.filterType === FilterType.Summary) {
                return (
                    <IventisErrorBoundaryWithToast toast={toast} action="reload" size="large">
                        <IGridComponent
                            xGroups={xGroups}
                            rowHeaderGroups={yGroups}
                            items={summaryGridItems}
                            rowHeadersWidth={120}
                            columnHeadersHeight={50}
                            columnWidth={90}
                            renderColumnHeader={(group) => renderSummaryColumnHeader(group as SummaryColumnGroup, schedule.timezone)}
                            renderFirstLevelRowHeader={renderSummaryCluster}
                            renderSecondLevelRowHeader={renderSummaryVenue}
                            renderThirdLevelRowHeader={renderSummaryDiscipline}
                            renderOriginHeader={RenderOriginHeader}
                            colours={theme.summaryColours}
                            renderItem={(eventBlock: IGridItemEventBlock, suppressClickEvent: boolean) =>
                                renderGridItem(
                                    eventBlock,
                                    showSessions,
                                    suppressClickEvent,
                                    schedule.timezone,
                                    showSessions
                                        ? ({ clientX: x, clientY: y }) => {
                                              const sessionEventBlock = findHierarchyEntityById([state.context.eventBlockHierarchy], eventBlock.id) as Hierarchy<EventBlock>;
                                              send({ type: "SET_SELECTED_SUB_SCHEDULES", payload: [sessionEventBlock.item.scheduleVersionId] });
                                              renderPopUp(x, y, sessionEventBlock);
                                          }
                                        : undefined
                                )
                            }
                        />
                    </IventisErrorBoundaryWithToast>
                );
            }

            if (state.context.selectedViewByOption?.filterType === FilterType.VenueRunSheet) {
                return (
                    <IventisErrorBoundaryWithToast toast={toast} action="reload" size="large">
                        <StyledTableWrapper>
                            <RunSheet
                                machineContext={state.context}
                                updateEventBlockHierarchy={send}
                                loadingBar={loadingBar}
                                timezone={schedule.timezone}
                                permission={scheduleVersionPermission}
                            />
                        </StyledTableWrapper>
                    </IventisErrorBoundaryWithToast>
                );
            }

            if (xGroups.length === 0) {
                const selection = (state.context.selectedViewByOption.dataField?.name || state.context.selectedViewByOption.filterType) as "Day" | "Discipline";
                return (
                    <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Attention>{translate(Content.schedule2.other.no_items_based_on_selection[selection])}</Attention>
                    </div>
                );
            }

            return (
                <IventisErrorBoundaryWithToast toast={toast} action="reload" size="large">
                    <StyledSchedule
                        data={calendarSessions}
                        groups={xGroups}
                        minCategoryWidth={150}
                        sessionComponent={SportSessionRenderer}
                        eventUnitComponent={SportEventUnitRenderer}
                        sessionTabFormatter={(session: Session) => session.name}
                        start={utcToZonedTime(calendarStartPosition, schedule.timezone)}
                        end={utcToZonedTime(calendarEndPosition, schedule.timezone)}
                        initialStart={urlStartPosition == null ? undefined : utcToZonedTime(urlStartPosition, schedule.timezone)}
                        initialEnd={urlEndPosition == null ? undefined : utcToZonedTime(urlEndPosition, schedule.timezone)}
                        onSessionClick={(session, event) => {
                            const clickedEventBlock = findHierarchyEntityById(eventBlockHierarchy.children, session.id) as Hierarchy<EventBlock>;
                            const sessionEventBlock = findTopCalendarlevelEventBlock(clickedEventBlock, eventBlockHierarchy.children);
                            send({ type: "SET_SELECTED_SUB_SCHEDULES", payload: [sessionEventBlock.item.scheduleVersionId] });
                            renderPopUp(event.clientX, event.clientY, sessionEventBlock);
                        }}
                        onCalendarClick={() => undefined}
                        onKeyButtonClicked={(e) => setKeyAnchor(e.target)}
                        keyOpen={keyAnchor != null}
                        showSubEvents={showEventUnits}
                        onTimeChange={({ start, end }) => {
                            const { query } = locationData$.getValue();
                            if (isFilterTypeCalendar(query.get(ScheduleQueryParam.ViewBy))) {
                                // We first check the view by filter because we may have switched away from the calendar view by the time this callback is ran
                                query.set(ScheduleQueryParam.CalendarStart, dateToTimeSpan(start));
                                query.set(ScheduleQueryParam.CalendarEnd, dateToTimeSpan(end));
                                updateUrlSearchParam(history, query);
                            }
                        }}
                        calendarTheme={{ iconButtonColours: { idle: theme.primaryColors.subdued70, selected: theme.primaryColors.focus } }}
                        translations={{ resetZoom: translate(Content.schedule3.resetZoom) }}
                    />
                </IventisErrorBoundaryWithToast>
            );
        }

        if (isTableView) {
            return (
                <IventisErrorBoundaryWithToast toast={toast} action="reload" size="large">
                    <StyledTableWrapper>
                        <ScheduleTable
                            machineContext={state.context}
                            updateEventBlock={(eventBlock) => send("UPDATE_EVENT_BLOCK", { payload: eventBlock })}
                            deleteEventBlock={(id: string) => send({ type: "DELETE_EVENT_BLOCK", payload: id })}
                            showEventUnits={showEventUnits}
                            timezone={schedule.timezone}
                        />
                    </StyledTableWrapper>
                </IventisErrorBoundaryWithToast>
            );
        }

        throw new Error("Unhandled state");
    }, [
        isLoading,
        isErrored,
        noSchedule,
        waitingForScheduleChanges,
        translate,
        isTableView,
        state.context.selectedViewByOption,
        showEventUnits,
        calendarSessions,
        calendarStartPosition,
        summaryGridItems,
    ]);

    const [sidebarWidth, setSideBarWidth] = useState(0);
    const [showSidebar, setShowSidebar] = useState(false);
    // Confirmation of delete schedule version text

    // if show side bar changes we need to set the width
    useEffect(() => {
        if (showSidebar) {
            if (screenWidth < screenSizeBreakpoints.small) {
                setSideBarWidth(screenWidth);
            } else {
                setSideBarWidth(300);
            }
        } else {
            setSideBarWidth(0);
        }
    }, [showSidebar, screenWidth]);

    const toggleRepresentation = useCallback(() => {
        if (!isTableView) {
            setIsTableView(true);
        }
        if (isTableView) {
            setIsTableView(false);
        }
    }, [isTableView]);

    const currentSchedule = state.context?.availableSchedules?.children.find((x) => x.item.id === state.context.selectedScheduleVersionHierarchy?.item.id)?.item;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const flattenedVenueLocations = useMemo(() => flattenVenueLocations(venueLocations, schedule?.config?.venueLocationTypes), [
        venueLocations,
        schedule?.config?.venueLocationTypes,
    ]);

    const showCreateSessionModal = Boolean(wizard?.type === ScheduleWizardDisplayed.CREATE_SESSION && selectedScheduleNode && selectedSubScheduleIds);

    const showEditSessionModal = Boolean(wizard?.type === ScheduleWizardDisplayed.EDIT_SESSION && selectedScheduleNode);

    const showDaySelector =
        selectedViewByOption?.filterType === FilterType.DayVenue ||
        selectedViewByOption?.filterType === FilterType.DayDataField ||
        selectedViewByOption?.filterType === FilterType.VenueRunSheet;

    const showDayVenueSelector = selectedViewByOption?.filterType === FilterType.DayVenue || selectedViewByOption?.filterType === FilterType.Summary;

    const showVenueSelector = selectedViewByOption?.filterType === FilterType.Venue || selectedViewByOption?.filterType === FilterType.VenueRunSheet;

    // Selector not shown when in table view as it does not function in this view
    const showDayDataFieldSelector = selectedViewByOption?.filterType === FilterType.DayDataField && !isTableView;

    const showScheduledItemsToggle =
        (!isTableView || isTableView) && state.context.selectedViewByOption?.filterType !== FilterType.Summary && selectedViewByOption?.filterType !== FilterType.VenueRunSheet;

    const showSessionsToggle = (!isTableView || isTableView) && state.context.selectedViewByOption?.filterType === FilterType.Summary;

    const handleSidebarChange = () => {
        setShowSidebar(!showSidebar);
    };

    const downloadDataTable = async () => {
        const baseUrl = getApiBaseUrl(MicroService.SCHEDULE);
        const filter: FilterFormat[] = [
            {
                fieldName: "eventBlockType",
                operator: IventisFilterOperator.Eq,
                value: showEventUnits
                    ? getEventBlockTypeByCalendarLevel(schedule.config.eventBlockTypes, 2)?.name
                    : getEventBlockTypeByCalendarLevel(schedule.config.eventBlockTypes, 1)?.name,
            },
            {
                fieldName: showEventUnits ? "parentVenueLocationId" : "venueLocationId",
                operator: IventisFilterOperator.In,
                value: selectedVenueLocations.map(({ item }) => item.id),
            },
        ];
        if (state.context.selectedViewByOption?.filterType === FilterType.DayDataField || state.context.selectedViewByOption?.filterType === FilterType.DayVenue) {
            filter.push({
                fieldName: "date",
                operator: IventisFilterOperator.Eq,
                value: state.context.availableDays.get(day),
            });
        }
        const exportUrl = `${baseUrl}event-blocks/schedule-versions/${state.context.selectedScheduleVersionRootId}/export?${
            queryParams[API_KEY_QUERY_PARAM_KEY] == null ? "" : `&&api-key=${queryParams[API_KEY_QUERY_PARAM_KEY]}`
        }`;

        const csvPost = await scheduleApi.post(exportUrl, filter, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.common.csv) }),
                message: translate(Content.errors.custom_network_error.create.message, { entity: translate(Content.common.csv) }),
            },
        });
        const blockType = filter.find((f) => f.fieldName === "eventBlockType");

        downloadCSV(csvPost.data, blockType?.value ? `${blockType.value}.csv` : "schedule.csv");
    };

    return (
        <StyledPage>
            {showSidebar && (
                <IventisErrorBoundaryWithToast toast={toast} action="reload" size="large">
                    <ScheduleSidebar
                        scheduleContext={state.context}
                        setWizard={setWizard}
                        close={closeSidebar}
                        onScheduleVisibilityChanged={onScheduleVisibilityChanged}
                        isErrored={isErrored}
                        isLoading={isLoading}
                        selectedScheduleNode={selectedScheduleNode}
                        send={send}
                        width={sidebarWidth}
                    />
                </IventisErrorBoundaryWithToast>
            )}
            <StyledMainSection showSidebar={showSidebar} sidebarWidth={sidebarWidth}>
                {!(noSchedule || waitingForScheduleChanges) && (
                    <HeadSection showSideBar={showSidebar}>
                        <StyledGroupedRow>
                            <div className="grouped-selectors-stacked stretch-stacked margin-bottom-stacked">
                                <SideBarToggleComponent
                                    showSidebar={showSidebar}
                                    scheduleName={currentSchedule?.name || ""}
                                    scheduleColour={getScheduleVersionColour(currentSchedule)}
                                    onClick={handleSidebarChange}
                                    disabled={isLoading || isErrored}
                                />
                                {/* VIEW BY SELECTOR */}
                                {selectedViewByOption != null && (
                                    <StyledViewBy
                                        selection={selectedViewByOption}
                                        onChange={(selection) => send({ type: "UPDATE_VIEW_BY", payload: selection })}
                                        options={availableViewByOptions}
                                        disabled={isLoading || isErrored}
                                    />
                                )}
                            </div>

                            {(showDaySelector || showDayVenueSelector || showVenueSelector) && (
                                <div className="grouped-selectors-stacked stretch-stacked">
                                    {/* DAY SELECTOR */}
                                    {showDaySelector && (
                                        <StyledDaySelector
                                            dayNumber={day}
                                            days={availableDays}
                                            onChange={(newDay: number) => send({ type: "UPDATE_DAY", payload: newDay })}
                                            disabled={isLoading || isErrored}
                                            timezone={schedule.timezone}
                                        />
                                    )}

                                    {/* VENUE SELECTOR */}
                                    {(showDayVenueSelector || showVenueSelector) && (
                                        <VenueSelectorButton selectedVenueLocations={selectedVenueLocations} flattenedVenueLocations={flattenedVenueLocations}>
                                            <VenueSelector
                                                venueLocations={venueLocations}
                                                selectedVenueLocations={selectedVenueLocations}
                                                singleSelectionOnly={showVenueSelector}
                                                configIncludesClusters={includeThirdLevelVenue(schedule?.config?.venueLocationTypes)}
                                                updateSelection={(selection) => send({ type: "UPDATE_SELECTED_VENUE_LOCATIONS", payload: selection })}
                                                isLoadingEventBlocks={isLoading}
                                            />
                                        </VenueSelectorButton>
                                    )}

                                    {/* DAY DATA FIELD SELECTOR */}
                                    {showDayDataFieldSelector && (
                                        <StyledSelector
                                            multi
                                            disabled={isLoading || isErrored}
                                            renderLabel={() =>
                                                renderDataFieldSelectedText(
                                                    selectedViewByOption.dataField?.name,
                                                    selectedDataFieldListItems,
                                                    availableDataFieldListItems,
                                                    translate
                                                )
                                            }
                                            options={availableDataFieldListItems.map(({ id, name, metaData }) => ({ id, label: `${metaData.code} - ${name}` }))}
                                            values={selectedDataFieldListItems.map((item) => item.id)}
                                            onChange={(selection: string[]) => {
                                                // if the last element of the selection is all
                                                if (selection.some((selection) => selection === allOptionsValueLiteral)) {
                                                    // check if all the values are selected if so unselect else select all
                                                    send({
                                                        type: "UPDATE_SELECTED_DATA_FIELD_LIST_ITEMS",
                                                        payload: availableDataFieldListItems.length === selectedDataFieldListItems.length ? [] : availableDataFieldListItems,
                                                    });
                                                } else {
                                                    send({
                                                        type: "UPDATE_SELECTED_DATA_FIELD_LIST_ITEMS",
                                                        payload: availableDataFieldListItems.filter(({ id }) => selection.includes(id)),
                                                    });
                                                }
                                            }}
                                            toggleAllOptions
                                            toggleAllOptionsLabel={`(${translate(Content.common.buttons.select_all)})`}
                                        />
                                    )}
                                </div>
                            )}

                            {/* DATA FIELD SELECTOR */}
                            {selectedViewByOption?.filterType === FilterType.DataField && selectedDataFieldListItems !== undefined && availableDataFieldListItems.length > 0 && (
                                <StyledDataFieldSelector variant="outlined" className="margin-bottom-stacked stretch-stacked">
                                    <Select
                                        data-cy="schedule-data-field-selector"
                                        disabled={isLoading || isErrored}
                                        input={<StyledItemSelect $verticalPadding="8px" />}
                                        value={selectedDataFieldListItems[0].id}
                                        onChange={(e: SelectChangeEvent) => {
                                            send({
                                                type: "UPDATE_SELECTED_DATA_FIELD_LIST_ITEMS",
                                                payload: [availableDataFieldListItems.find(({ id }) => id === e.target.value)],
                                            });
                                        }}
                                        renderValue={() => <span>{`${selectedDataFieldListItems[0].metaData.code} - ${selectedDataFieldListItems[0].name}`}</span>}
                                    >
                                        {availableDataFieldListItems.map((item) => (
                                            <StyledMenuItem data-cy={item.name} key={item.id} value={item.id}>
                                                {item.metaData.code} - {item.name}
                                            </StyledMenuItem>
                                        ))}
                                    </Select>
                                </StyledDataFieldSelector>
                            )}

                            <div className="right-hand-selectors">
                                {/* SCHEDULED ITEMS TOGGLE */}
                                {showScheduledItemsToggle && (
                                    <SwitchWithLabel
                                        dataCy="scheduled-items-toggle"
                                        checked={showEventUnits}
                                        onChange={() => setCalendarLevel(calendarLevel === 1 ? 2 : 1)}
                                        label={Content.schedule.show_scheduled_items}
                                        disabled={isLoading || isErrored}
                                    />
                                )}

                                {/* SHOW SESSIONS TOGGLE */}
                                {showSessionsToggle && (
                                    <SwitchWithLabel
                                        dataCy="schedule-sessions-toggle"
                                        checked={showSessions}
                                        onChange={() => setShowSessions(!showSessions)}
                                        label={Content.schedule.show_sessions}
                                        disabled={isLoading || isErrored}
                                    />
                                )}

                                {/* DOWNLOAD TABLE BUTTON */}
                                {isTableView && (
                                    <StyledCalendarHeaderButtonLarge variant="outlined" onClick={downloadDataTable} disabled={isLoading}>
                                        <FontAwesomeIcon icon={["fas", "file-arrow-down"]} />
                                    </StyledCalendarHeaderButtonLarge>
                                )}

                                {/* TABLE/ CALENDAR TOGGLE */}
                                {selectedViewByOption?.filterType !== FilterType.VenueRunSheet && (
                                    <StyledCalendarHeaderButtonLarge data-cy="schedule-table-toggle" variant="outlined" onClick={toggleRepresentation} disabled={isLoading}>
                                        <FontAwesomeIcon icon={["fas", representationToggleIcons.get(isTableView)]} />
                                    </StyledCalendarHeaderButtonLarge>
                                )}
                            </div>
                        </StyledGroupedRow>

                        {/* The above headers, but for small displays */}
                        <StyledContentHeaders>
                            {/* SCHEDULED ITEMS TOGGLE */}
                            {showScheduledItemsToggle && (
                                <SwitchWithLabel
                                    dataCy="scheduled-items-toggle"
                                    checked={showEventUnits}
                                    onChange={() => setCalendarLevel(calendarLevel === 1 ? 2 : 1)}
                                    label={Content.schedule.show_scheduled_items}
                                    width="auto"
                                    disabled={isLoading || isErrored}
                                />
                            )}

                            {/* SHOW SESSIONS TOGGLE */}
                            {showSessionsToggle && (
                                <SwitchWithLabel
                                    dataCy="schedule-sessions-toggle"
                                    checked={showSessions}
                                    onChange={() => setShowSessions(!showSessions)}
                                    label={Content.schedule.show_sessions}
                                    width="auto"
                                    disabled={isLoading || isErrored}
                                />
                            )}

                            <Divider orientation="vertical" flexItem style={{ height: "25px", marginTop: "11px" }} />

                            {/* DOWNLOAD TABLE BUTTON */}
                            {isTableView && (
                                <StyledCalendarHeaderButtonSmall variant="outlined" onClick={downloadDataTable} disabled={isLoading}>
                                    <FontAwesomeIcon icon={["fas", "file-arrow-down"]} />
                                </StyledCalendarHeaderButtonSmall>
                            )}

                            {/* TABLE/ CALENDAR TOGGLE */}
                            {selectedViewByOption?.filterType !== FilterType.VenueRunSheet && (
                                <StyledCalendarHeaderButtonSmall data-cy="schedule-table-toggle" variant="outlined" onClick={toggleRepresentation} disabled={isLoading}>
                                    <FontAwesomeIcon icon={["fas", representationToggleIcons.get(isTableView)]} />
                                </StyledCalendarHeaderButtonSmall>
                            )}
                        </StyledContentHeaders>
                    </HeadSection>
                )}
                <StyledMainContentContainer>
                    {MainContent}
                    {selectedViewByOption?.filterType !== FilterType.VenueRunSheet && (
                        <div className="add-session-btn">
                            {selectedScheduleNode && (
                                <IconButtonComponent
                                    height={30}
                                    width={30}
                                    disabled={!canEdit(lowestAuthorisation(selectedScheduleNode.childNodes, selectedSubScheduleIds))}
                                    onClick={() => setWizard({ type: ScheduleWizardDisplayed.CREATE_SESSION })}
                                    hiddenText={translate(Content.schedule.create_session_form.create_new_session)}
                                >
                                    <FontAwesomeIcon icon={["far", "plus"]} color={styledTheme.secondaryColors.blank} style={{ height: "1rem", width: "1rem" }} />
                                </IconButtonComponent>
                            )}
                        </div>
                    )}
                    {(selectedViewByOption?.filterType === FilterType.VenueRunSheet || selectedViewByOption?.filterType === FilterType.Summary) && (
                        <div className="key-selector">
                            <CalendarButtonsContainer color="inherit" orientation="vertical" variant="text">
                                <CalendarButton
                                    onClick={(e: BaseSyntheticEvent) => setKeyAnchor(e.target)}
                                    style={{ color: keyAnchor == null ? "inherit" : theme.primaryColors.focus }}
                                >
                                    <FontAwesomeIcon style={{ pointerEvents: "none" }} icon={["fas", "key"]} />
                                </CalendarButton>
                            </CalendarButtonsContainer>
                        </div>
                    )}
                </StyledMainContentContainer>

                <StyledCustomDialog
                    open={Boolean(wizard)}
                    onClose={() => setWizard(null)}
                    $screenWidth={screenWidth}
                    $alternativePadding={showCreateSessionModal || showEditSessionModal}
                    $staticHeight={wizard?.type !== ScheduleWizardDisplayed.IMPORT_EVENT_BLOCKS ? "80vh" : undefined}
                    $minWidth={wizard?.type === ScheduleWizardDisplayed.ADD_DATAFIELDS ? (screenWidth <= screenSizeBreakpoints.extraLarge ? "80vw" : "60vw") : null}
                >
                    {wizard?.type === ScheduleWizardDisplayed.CREATE_UNIT && (
                        <SubEventDialog
                            venueHierarchy={venueLocations}
                            allDataFields={schedule.config.dataFields}
                            eventBlockType={schedule.config.eventBlockTypes.find(({ calendarLevel }) => calendarLevel === 2)}
                            session={selectedSession}
                            scheduleVersion={findHierarchyEntityById([selectedScheduleVersionHierarchy], selectedSession.item.scheduleVersionId) as Hierarchy<ScheduleVersion>}
                            sendToScheduleMachine={send}
                            close={() => setWizard(null)}
                            crudOperation={SubEventCrudOperation.CREATE}
                            eventBlockTypes={state.context.schedule.config.eventBlockTypes}
                            scheduleDaysMap={state.context.availableDays}
                            currentDate={state.context.availableDays.get(day)}
                            timezone={schedule.timezone}
                        />
                    )}
                    {wizard?.type === ScheduleWizardDisplayed.EDIT_UNIT && (
                        <SubEventDialog
                            existingEvent={findHierarchyEntityById([eventBlockHierarchy], wizard.id) as Hierarchy<EventBlock>}
                            venueHierarchy={venueLocations}
                            allDataFields={schedule.config.dataFields}
                            eventBlockType={schedule.config.eventBlockTypes.find(({ calendarLevel }) => calendarLevel === 2)}
                            session={selectedSession}
                            scheduleVersion={findHierarchyEntityById([selectedScheduleVersionHierarchy], selectedSession.item.scheduleVersionId) as Hierarchy<ScheduleVersion>}
                            sendToScheduleMachine={send}
                            close={() => setWizard(null)}
                            crudOperation={SubEventCrudOperation.EDIT}
                            eventBlockTypes={state.context.schedule.config.eventBlockTypes}
                            scheduleDaysMap={state.context.availableDays}
                            currentDate={state.context.availableDays.get(day)}
                            timezone={schedule.timezone}
                        />
                    )}
                    {wizard?.type === ScheduleWizardDisplayed.CREATE_SUB_SCHEDULE && (
                        <CreateSubScheduleComponent
                            parentSchedule={{
                                ...scheduleNode.childNodes.find((node) => node.id === selectedScheduleVersionRootId),
                                visible: false,
                                childNodes: [],
                            }}
                            parentScheduleVersion={
                                state.context.availableSchedules.children.find((scheduleVersion) => scheduleVersion.item.id === selectedScheduleVersionRootId).item
                            }
                            scheduleId={schedule.id}
                            piblings={scheduleNode.childNodes}
                            close={() => setWizard(null)}
                            scheduleVersionType={schedule.config.scheduleVersionTypes.find((sv) => sv.allowedParentType !== EMPTY_GUID)}
                            dataField={schedule.config.dataFields.find((df) => df.name === SportEventDataFieldNames.DISCIPLINE)}
                            addScheduleVersions={(scheduleVersions, copyPending) => {
                                if (copyPending) {
                                    const { parentId } = scheduleVersions[0];
                                    // Open the resource on WS so we can listen for any copies
                                    setOpenResources([{ resourceId: parentId, sourceId: parentId, resourceType: ResourceTypes.SCHEDULE_VERSION }]);
                                    send({
                                        type: "WAIT_FOR_SCHEDULE_VERSION_CHILDREN_COPY",
                                        payload: {
                                            rootScheduleVersion: { id: parentId, name: scheduleNode.childNodes.find((node) => node.id === selectedScheduleVersionRootId).name },
                                            scheduleVersionsPending: scheduleVersions.map(({ name }) => ({ name })),
                                            rootNodeExists: true,
                                            isRootScheduleCreation: false,
                                        },
                                    });
                                } else {
                                    // Do not wait for anything if there are no copies
                                    send("ADD_SCHEDULE_VERSIONS", { payload: scheduleVersions });
                                }
                            }}
                        />
                    )}
                    {wizard?.type === ScheduleWizardDisplayed.CREATE_SCHEDULE && (
                        <CreateScheduleFormWizard
                            close={() => setWizard(null)}
                            piblings={scheduleNode.childNodes}
                            schedule={schedule}
                            onFinish={() => undefined}
                            onWaitingForCopy={(scheduleVersion, names) => {
                                send({ type: "ADD_SCHEDULE_VERSIONS", payload: [scheduleVersion] });
                                // Open the resource on WS so we can listen for children copies
                                setOpenResources([{ resourceId: scheduleVersion.id, sourceId: scheduleVersion.id, resourceType: ResourceTypes.SCHEDULE_VERSION }]);
                                // Notify the user that we are handling their request
                                toast.info({
                                    title: translate(Content.common.alert),
                                    message: translate(Content.common.toasts.creating_resource, { resource: scheduleVersion.name }),
                                });

                                send({
                                    type: "WAIT_FOR_SCHEDULE_VERSION_CHILDREN_COPY",
                                    payload: { rootScheduleVersion: scheduleVersion, scheduleVersionsPending: names, isRootScheduleCreation: true, rootNodeExists: false },
                                });

                                // Select the new schedule
                                send({ type: "SELECT_ROOT_SCHEDULE_VERSION", payload: scheduleVersion });

                                const alreadyCreated = historicalNodeCreatedEvents.find((node) => node.id === scheduleVersion.id);
                                if (alreadyCreated) {
                                    // If the node was already created, re-dispatch the creation event now we have added the schedule version, since the first time it happened, we weren't ready for it
                                    send({ type: "SCHEDULE_VERSION_NODE_CREATED", payload: alreadyCreated });
                                }
                            }}
                        />
                    )}
                    {showCreateSessionModal && (
                        <CreateSessionComponent
                            schedule={schedule}
                            scheduleVersions={state.context.scheduleNode.childNodes.find((node) => node.id === state.context.selectedScheduleVersionRootId).childNodes}
                            selectedSubSchedule={selectedSubSchedule?.item}
                            close={() => {
                                setWizard(null);
                                setDialogSelectedHierarchy(null);
                            }}
                            selectedScheduleNode={selectedScheduleNode}
                            selectedSubScheduleId={selectedSubScheduleIds.length === 1 ? selectedSubScheduleIds[0] : null}
                            isScheduleManager
                            sessionsInSelectedSchedule={state.context.eventBlockHierarchy.children}
                            venues={state.context.venueLocations}
                            selectedVenues={state.context.selectedVenueLocations}
                            day={showDaySelector && day}
                            sendToScheduleMachine={send}
                            timezone={schedule.timezone}
                        />
                    )}
                    {showEditSessionModal && (
                        <CreateSessionComponent
                            existingSession={dialogSelectedHierarchy}
                            schedule={schedule}
                            scheduleVersions={state.context.scheduleNode.childNodes.find((node) => node.id === state.context.selectedScheduleVersionRootId).childNodes}
                            selectedSubSchedule={selectedSubSchedule?.item}
                            close={() => {
                                setWizard(null);
                                setDialogSelectedHierarchy(null);
                            }}
                            selectedScheduleNode={selectedScheduleNode}
                            selectedSubScheduleId={selectedSubScheduleIds.length === 1 ? selectedSubScheduleIds[0] : null}
                            isScheduleManager
                            sessionsInSelectedSchedule={state.context.eventBlockHierarchy.children}
                            venues={venueLocations}
                            selectedVenues={state.context.selectedVenueLocations}
                            day={showDaySelector && day}
                            sendToScheduleMachine={send}
                            timezone={schedule.timezone}
                        />
                    )}
                    {wizard?.type === ScheduleWizardDisplayed.EDIT_SCHEDULE && (
                        <ScheduleEditModal node={findNode(scheduleNode.childNodes, wizard.id) as Node} close={() => setWizard(null)} sendToScheduleMachine={send} />
                    )}
                    {wizard?.type === ScheduleWizardDisplayed.ADD_DATAFIELDS && (
                        <EditScheduleDataOptions
                            venueLocationTypes={schedule.config.venueLocationTypes}
                            scheduleId={schedule.id}
                            dataFields={schedule.config.dataFields}
                            close={() => setWizard(null)}
                            onDismount={(hasDataChanged) => {
                                if (hasDataChanged) {
                                    send("REFRESH_SCHEDULE");
                                }
                            }}
                        />
                    )}
                    {/* Import wizard is fine to take this parameter on refresh, it only refreshes once a table is uploaded, therefore clicking the background to not refresh is fine */}
                    {wizard?.type === ScheduleWizardDisplayed.IMPORT_EVENT_BLOCKS && (
                        <ImportEventBlocks
                            close={(refresh) => {
                                if (refresh) {
                                    send({ type: "GET_EVENT_BLOCK_HIERARCHY" });
                                }
                                setWizard(null);
                            }}
                            eventBlockTypes={schedule.config.eventBlockTypes}
                            defaultScheduleVersionId={selectedSubScheduleIds?.[0]}
                            scheduleVersionNodeHierarchy={selectedScheduleNode}
                            rootScheduleVersionId={selectedScheduleVersionRootId}
                        />
                    )}
                </StyledCustomDialog>
            </StyledMainSection>
            {miniPopOver?.eventBlockHierarchySelected && (
                <PopOverByCords x={miniPopOver.x} y={miniPopOver.y} onExit={() => setMiniPopOver(null)}>
                    <SportSessionCard
                        onEdit={() => {
                            setDialogSelectedHierarchy(miniPopOver.eventBlockHierarchySelected);
                            setWizard({ type: ScheduleWizardDisplayed.EDIT_SESSION });
                        }}
                        onEditEventBlockChild={(id) => {
                            setDialogSelectedHierarchy(miniPopOver.eventBlockHierarchySelected);
                            setWizard({ type: ScheduleWizardDisplayed.EDIT_UNIT, id });
                        }}
                        onCreateEventBlockChild={() => {
                            setDialogSelectedHierarchy(miniPopOver.eventBlockHierarchySelected);
                            setWizard({ type: ScheduleWizardDisplayed.CREATE_UNIT });
                        }}
                        eventBlockHierarchy={miniPopOver.eventBlockHierarchySelected}
                        venueLocations={
                            getParentHierarchyTreeByIdAsArray(state.context.venueLocations, miniPopOver.eventBlockHierarchySelected.item.venueLocation.id) as VenueLocation[]
                        }
                        dayNumber={dayNumberFromDate(availableDays, new Date(miniPopOver.eventBlockHierarchySelected.item.startAt))}
                        scheduleVersions={
                            getParentHierarchyTreeByIdAsArray(
                                [state.context.selectedScheduleVersionHierarchy],
                                miniPopOver.eventBlockHierarchySelected.item.scheduleVersionId
                            ) as ScheduleVersion[]
                        }
                        onClose={() => setMiniPopOver(null)}
                        onEditSession={canEdit((findNode([scheduleNode], miniPopOver.eventBlockHierarchySelected.item.scheduleVersionId) as Node).authorisation)}
                        timezone={schedule.timezone}
                    />
                </PopOverByCords>
            )}
            {keyAnchor != null && (
                <IventisErrorBoundaryWithToast toast={toast} size="overlay">
                    <KeyPopoverComponent keyAnchor={keyAnchor} setKeyAnchor={setKeyAnchor} config={schedule?.config} showEventUnits={showEventUnits} />
                </IventisErrorBoundaryWithToast>
            )}
            {children}
        </StyledPage>
    );
};

export default SchedulePage;
